import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, FileText, Send, MapPin, Lock, Globe2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCity } from '../contexts/CityContext';
import { useAccount } from '../contexts/AccountContext';
import { DistrictMap } from './requetes/DistrictMap';
import { districtsFor, districtPoints, sectorShare, type CityDistrict } from '../utils/districts';
import { sectorsFor } from '../data/communications';
import { requestPinsFor, type SlaState } from '../utils/requestPins';
import { RequestDetailPanel } from './requetes/RequestPinPopup';
import { AtnaDataAssistant } from './requetes/AtnaDataAssistant';
import { DEFAULT_RANGE, rangeDays, type DateRange } from './requetes/DateRangePicker';
import { TerritorialBriefingPanel } from './requetes/TerritorialBriefingPanel';
import { buildBriefing } from '../utils/territorialBriefing';
import { CouncilReportDialog } from './requetes/CouncilReportDialog';
import { DashboardKpis, type KpiTarget } from './requetes/DashboardKpis';
import { DashboardScope } from './requetes/DashboardScope';
import { fmtDec, fmtNum, statsFor, type AttentionTarget } from '../utils/requestDashboard';
import { ATNA_DATA_SUGGESTIONS } from '../utils/atnaDataAnswers';
import type { DatePreset } from './requetes/DateRangePicker';
import type { Localized } from '../types/notice';
const PERIOD_LABELS: Record<DatePreset, Localized> = {
  '7d': {
    fr: '7 derniers jours',
    en: 'Last 7 days'
  },
  '30d': {
    fr: '30 derniers jours',
    en: 'Last 30 days'
  },
  '90d': {
    fr: '3 derniers mois',
    en: 'Last 3 months'
  },
  ytd: {
    fr: 'Depuis le 1er janvier 2026',
    en: 'Since January 1, 2026'
  },
  custom: {
    fr: 'Période personnalisée',
    en: 'Custom period'
  }
};
// --- DATA ---
type Status = 'Sous la cible' | 'À surveiller' | 'Conforme';
interface ServiceData {
  id: string;
  service: string;
  serviceEn: string;
  category: string;
  categoryEn: string;
  target: string;
  targetEn: string;
  performance: number;
  status: Status;
}
const initialData: ServiceData[] = [{
  id: '1',
  service: 'Éclairage de parc',
  serviceEn: 'Park lighting',
  category: 'Éclairage',
  categoryEn: 'Lighting',
  target: '21 jours',
  targetEn: '21 days',
  performance: 89,
  status: 'Conforme'
}, {
  id: '2',
  service: 'Réparation lampadaire',
  serviceEn: 'Streetlight repair',
  category: 'Éclairage',
  categoryEn: 'Lighting',
  target: '14 jours',
  targetEn: '14 days',
  performance: 92,
  status: 'Conforme'
}, {
  id: '3',
  service: 'Signalisation routière',
  serviceEn: 'Road signage',
  category: 'Voirie',
  categoryEn: 'Roads',
  target: '180 jours',
  targetEn: '180 days',
  performance: 94,
  status: 'Conforme'
}, {
  id: '4',
  service: 'Nid-de-poule',
  serviceEn: 'Pothole',
  category: 'Voirie',
  categoryEn: 'Roads',
  target: '7 jours',
  targetEn: '7 days',
  performance: 78,
  status: 'À surveiller'
}, {
  id: '5',
  service: 'Réparation d’un bac brisé',
  serviceEn: 'Broken bin repair',
  category: 'Matières résiduelles',
  categoryEn: 'Waste management',
  target: '14 jours',
  targetEn: '14 days',
  performance: 96,
  status: 'Conforme'
}];
export interface ImpactedService {
  name: string;
  nameEn: string;
  requests: number;
  sla: number;
}
export interface District {
  id: string;
  name: string;
  nameEn: string;
  label: string;
  sla: number;
  totalRequests: number;
  reachable: number;
  activeCards: number;
  openRate: number;
  impactedServices: ImpactedService[];
  points: string;
  labelX: number;
  labelY: number;
}
const districts: District[] = [{
  id: 'A',
  name: 'Secteur A',
  nameEn: 'District A',
  label: 'A',
  sla: 86,
  totalRequests: 412,
  reachable: 83,
  activeCards: 4182,
  openRate: 71,
  impactedServices: [{
    name: 'Éclairage',
    nameEn: 'Lighting',
    requests: 163,
    sla: 84
  }, {
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 124,
    sla: 88
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 88,
    sla: 91
  }],
  points: '20,20 200,20 200,160 20,160',
  labelX: 110,
  labelY: 95
}, {
  id: 'B',
  name: 'Secteur B',
  nameEn: 'District B',
  label: 'B',
  sla: 90,
  totalRequests: 287,
  reachable: 76,
  activeCards: 3214,
  openRate: 64,
  impactedServices: [{
    name: 'Réparation lampadaire',
    nameEn: 'Streetlight repair',
    requests: 102,
    sla: 87
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 71,
    sla: 92
  }, {
    name: 'Graffitis',
    nameEn: 'Graffiti',
    requests: 48,
    sla: 90
  }],
  points: '200,20 400,20 400,160 200,160',
  labelX: 300,
  labelY: 95
}, {
  id: 'C',
  name: 'Secteur C',
  nameEn: 'District C',
  label: 'C',
  sla: 84,
  totalRequests: 196,
  reachable: 88,
  activeCards: 2876,
  openRate: 79,
  impactedServices: [{
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 58,
    sla: 81
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 47,
    sla: 88
  }, {
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 33,
    sla: 86
  }],
  points: '400,20 580,20 580,160 400,160',
  labelX: 490,
  labelY: 95
}, {
  id: 'L',
  name: 'Secteur L',
  nameEn: 'District L',
  label: 'L',
  sla: 88,
  totalRequests: 318,
  reachable: 81,
  activeCards: 3654,
  openRate: 68,
  impactedServices: [{
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 118,
    sla: 86
  }, {
    name: 'Éclairage de parc',
    nameEn: 'Park lighting',
    requests: 82,
    sla: 89
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 64,
    sla: 93
  }],
  points: '20,160 200,160 200,300 20,300',
  labelX: 110,
  labelY: 235
}, {
  id: 'M',
  name: 'Secteur M',
  nameEn: 'District M',
  label: 'M',
  sla: 84,
  totalRequests: 351,
  reachable: 79,
  activeCards: 3982,
  openRate: 66,
  impactedServices: [{
    name: 'Éclairage',
    nameEn: 'Lighting',
    requests: 134,
    sla: 82
  }, {
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 96,
    sla: 87
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 67,
    sla: 82
  }],
  points: '200,160 400,160 400,300 200,300',
  labelX: 300,
  labelY: 235
}, {
  id: 'O',
  name: 'Secteur O',
  nameEn: 'District O',
  label: 'O',
  sla: 89,
  totalRequests: 142,
  reachable: 91,
  activeCards: 2412,
  openRate: 82,
  impactedServices: [{
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 41,
    sla: 90
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 36,
    sla: 92
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 28,
    sla: 85
  }],
  points: '400,160 580,160 580,300 400,300',
  labelX: 490,
  labelY: 235
}, {
  id: 'R',
  name: 'Secteur R',
  nameEn: 'District R',
  label: 'R',
  sla: 91,
  totalRequests: 224,
  reachable: 84,
  activeCards: 2987,
  openRate: 73,
  impactedServices: [{
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 78,
    sla: 89
  }, {
    name: 'Graffitis',
    nameEn: 'Graffiti',
    requests: 52,
    sla: 92
  }, {
    name: 'Collecte des ordures',
    nameEn: 'Waste collection',
    requests: 41,
    sla: 86
  }],
  points: '20,300 300,300 300,400 20,400',
  labelX: 160,
  labelY: 355
}, {
  id: 'S',
  name: 'Secteur S',
  nameEn: 'District S',
  label: 'S',
  sla: 82,
  totalRequests: 187,
  reachable: 86,
  activeCards: 2614,
  openRate: 75,
  impactedServices: [{
    name: 'Signalisation routière',
    nameEn: 'Road signage',
    requests: 62,
    sla: 90
  }, {
    name: 'Nid-de-poule',
    nameEn: 'Pothole',
    requests: 49,
    sla: 84
  }, {
    name: 'Réparation d’un bac brisé',
    nameEn: 'Broken bin repair',
    requests: 31,
    sla: 88
  }],
  points: '300,300 580,300 580,400 300,400',
  labelX: 440,
  labelY: 355
}];
function getStatusForSla(sla: number): 'critical' | 'warning' | 'on-track' {
  if (sla < 60) return 'critical';
  if (sla < 80) return 'warning';
  return 'on-track';
}
// --- COMPONENTS ---
function formatScaled(value: number, factor: number, lang: string) {
  return Math.round(value * factor).toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA');
}

/** Tells the user which part of the territory their account can see. */
function ScopeBanner({
  name,
  role,
  district
}: {
  name: string;
  role: string;
  district: CityDistrict | null;
}) {
  const {
    t,
    lang
  } = useLanguage();
  if (!district) {
    return <div className="mt-2 inline-flex self-start items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 font-body" data-id="element-5527" data-mp="2oy2nd8aqyjrw">
        <Globe2 className="w-3.5 h-3.5 text-slate-400" data-id="element-5528" data-mp="pmi4f6vtipry" />
        <span data-id="element-5529" data-mp="uji8jc93xigf">
          <span className="font-semibold text-[#0F172A]" data-id="element-5530" data-mp="ul3331vf91ea">
            {t('Vue : toute la ville', 'View: whole city')}
          </span>{' '}
          · {name}, {role}
        </span>
      </div>;
  }
  const letters = district.sectors.map(s => s.label[lang].replace(/^(Secteurs?|Sectors?) /, ''));
  const list = letters.length > 1 ? `${letters.slice(0, -1).join(', ')} ${t('et', 'and')} ${letters[letters.length - 1]}` : letters[0];
  return <div role="status" className="mt-2 flex items-start gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 font-body" data-id="element-5531" data-mp="1yplob8idnfdv">
      <div className="w-8 h-8 rounded-lg bg-[#4F46E5] text-white flex items-center justify-center shrink-0" data-id="element-5532" data-mp="23ndqqwgnejwb">
        <Lock className="w-4 h-4" data-id="element-5533" data-mp="28fx2rozot6z0" />
      </div>
      <div className="flex flex-col gap-0.5" data-id="element-5534" data-mp="2de8ey6dgftke">
        <div className="text-sm font-bold text-[#0F172A]" data-id="element-5535" data-mp="2ddypql08hqfr">
          {t(`Vue limitée au ${district.label.fr}`, `View limited to ${district.label.en}`)}
        </div>
        <p className="text-sm text-slate-600 m-0" data-id="element-5536" data-mp="2meeppkhrqffv">
          {t(`${name}, ${role.charAt(0).toLowerCase()}${role.slice(1)} : vous voyez uniquement les requêtes de votre district (secteur${letters.length > 1 ? 's' : ''} ${list}). Les autres districts sont masqués.`, `${name}, ${role}: you only see requests from your district (sector${letters.length > 1 ? 's' : ''} ${list}). Other districts are hidden.`)}
        </p>
      </div>
    </div>;
}
function KPIGrid({
  factor = 1,
  seed = 0
}: {
  factor?: number;
  seed?: number;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const scoped = factor < 1;
  const onTime = scoped ? 93 + seed % 5 - 2 : 94;
  const below = 0;
  const watch = scoped ? seed % 2 : 1;
  const avgDays = scoped ? Math.round((3.8 + (seed % 7 - 3) / 10) * 10) / 10 : 3.8;
  const satisfaction = scoped ? Math.round((4.6 + (seed % 3 - 1) / 10) * 10) / 10 : 4.6;
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 content-start" data-id="element-5537" data-mp="1d73crjii2n3u">
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5538" data-mp="18f38jeved81g">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5539" data-mp="32n6y7u26gn0j">
          {t('Requêtes reçues', 'Requests received')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5540" data-mp="36o8zshjkhb5o">
          {formatScaled(60058, factor, lang)}
        </div>
        <div className="text-sm text-slate-500 font-body" data-id="element-5541" data-mp="2xwql6b29r3ep">
          {t('+11 % vs mois précédent', '+11% vs. previous month')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5542" data-mp="2iyocxnkaltr4">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5543" data-mp="2o042nsxf0ofu">
          {t('Résolues dans les délais', 'Resolved on time')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5544" data-mp="2exl1dd9a2jvp">
          {onTime} %
        </div>
        <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-5545" data-mp="86vbya3wvh08">
          {t('+10 points', '+10 points')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5546" data-mp="d5zjkwb6t6og">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5547" data-mp="nay1x41x9y8m">
          {t('Services sous la cible', 'Services below target')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5548" data-mp="w2hypkn01y0y">{below}</div>
        <div className={`text-sm font-medium font-body ${below > 0 ? 'text-red-600' : 'text-emerald-600'}`} data-id="element-5549" data-mp="rc1ijsrsnyaw">
          {below > 0 ? t('Action requise', 'Action required') : t('Aucune action', 'No action needed')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5550" data-mp="111bbfaugpl02">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5551" data-mp="1f12lx1lrsj9b">
          {t('Services à surveiller', 'Services to watch')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5552" data-mp="3gspf99t4wq8y">{watch}</div>
        <div className="text-sm text-amber-600 font-medium font-body" data-id="element-5553" data-mp="3gusd2g0j3no8">
          {t('Tendance à la baisse', 'Trending down')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5554" data-mp="3e7hqw71m70y0">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5555" data-mp="3j6b21stfifv0">
          {t('Délai moyen de résolution', 'Average resolution time')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5556" data-mp="3s8c20xg1rg0d">
          {avgDays.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')} {t('j', 'd')}
        </div>
        <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-5557" data-mp="2vfylxuo15ctc">
          {t('−0,6 j vs mois précédent', '−0.6 d vs. previous month')}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-5558" data-mp="34hy44qas6mxv">
        <div className="text-sm font-medium text-slate-500 font-body" data-id="element-5559" data-mp="39a80op6eil94">
          {t('Satisfaction citoyenne', 'Citizen satisfaction')}
        </div>
        <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-5560" data-mp="2gt5f7dsktfcr">
          {satisfaction.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
          <span className="text-lg text-slate-400 font-semibold" data-id="element-5561" data-mp="298bdm1l8a9t0"> / 5</span>
        </div>
        <div className="text-sm text-slate-500 font-body" data-id="element-5562" data-mp="24bkyphl5tk96">
          {t('Sondage après fermeture · 38 % de réponse', 'Post-closure survey · 38% response')}
        </div>
      </div>
    </div>;
}
// --- PER-SECTOR DATA ---
/** Stable pseudo-random seed from a sector id, so figures don't change between renders. */
function hashOf(value: string) {
  let h = 0;
  for (let i = 0; i < value.length; i++) {
    h = h * 31 + value.charCodeAt(i) >>> 0;
  }
  return h;
}
function statusForPerformance(p: number): Status {
  if (p < 60) return 'Sous la cible';
  if (p < 80) return 'À surveiller';
  return 'Conforme';
}
/** Every service's SLA performance inside one sector. */
function sectorServices(sectorId: string): ServiceData[] {
  const h = hashOf(sectorId);
  return initialData.map((row, i) => {
    const offset = (h >>> i * 3) % 9 - 4;
    const performance = Math.max(18, Math.min(98, row.performance + offset));
    return {
      ...row,
      performance,
      status: statusForPerformance(performance)
    };
  });
}
function sectorRequests(sectorId: string) {
  // ~71 per sector on average, so 18 sectors ≈ the 1 284 requests of the period.
  return 42 + hashOf(sectorId) % 58;
}
/** bciti+ digital reach — real figures when the sector has data, otherwise stable estimates. */
function sectorReach(sectorId: string, letter: string) {
  const d = districts.find(x => x.label === letter);
  if (d) {
    return {
      reachable: d.reachable,
      activeCards: d.activeCards,
      openRate: d.openRate
    };
  }
  const h = hashOf(sectorId);
  return {
    reachable: 62 + h % 28,
    activeCards: 1800 + h % 3200,
    openRate: 55 + (h >>> 4) % 30
  };
}
/** Sector letter, e.g. "Secteur L" → "L". */
function letterOf(label: string) {
  return label.replace(/^Secteurs? /, '');
}
function PerformanceTable({
  rows
}: {
  rows: ServiceData[];
}) {
  const {
    t,
    lang
  } = useLanguage();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof ServiceData;
    direction: 'asc' | 'desc';
  }>({
    key: 'performance',
    direction: 'asc'
  });
  const handleSort = (key: keyof ServiceData) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({
      key,
      direction
    });
  };
  const data = [...rows].sort((a, b) => {
    const {
      key,
      direction
    } = sortConfig;
    if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
    return 0;
  });
  const getStatusColor = (status: Status) => {
    switch (status) {
      case 'Sous la cible':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'À surveiller':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Conforme':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
  };
  const statusLabel = (status: Status) => {
    switch (status) {
      case 'Sous la cible':
        return t('Sous la cible', 'Below target');
      case 'À surveiller':
        return t('À surveiller', 'To watch');
      case 'Conforme':
        return t('Conforme', 'On track');
    }
  };
  const getBarColor = (performance: number) => {
    if (performance < 60) return 'bg-red-500';
    if (performance < 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: keyof ServiceData;
  }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 text-slate-300 ml-1 inline-block" data-id="element-5563" data-mp="1s6f9ta2yrqzn" />;
    return sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3 text-slate-600 ml-1 inline-block" data-id="element-5564" data-mp="218yapon6vtj9" /> : <ArrowDown className="w-3 h-3 text-slate-600 ml-1 inline-block" data-id="element-5565" data-mp="216u8rc8up4lm" />;
  };
  return <div className="overflow-x-auto" data-id="element-5566" data-mp="2v51clioqqb6">
      <table className="w-full text-left text-sm whitespace-nowrap font-body" data-id="element-5567" data-mp="6unl3dyrsvcf">
        <thead className="bg-slate-50 border-y border-slate-200 text-slate-600 text-xs" data-id="element-5568" data-mp="6wqk2nhwhyta">
          <tr data-id="element-5569" data-mp="3vive23302szu">
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('service')} data-id="element-5570" data-mp="3qq27d9nz1al4">
              {t('Service', 'Service')} <SortIcon columnKey="service" data-id="element-5571" data-mp="3hpnmgbex5xi5" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('target')} data-id="element-5572" data-mp="3hr8enudrh4cu">
              {t('SLA', 'SLA')} <SortIcon columnKey="target" data-id="element-5573" data-mp="d92wyto8u5sy" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('performance')} data-id="element-5574" data-mp="98j1dujbja8x">
              {t('SLA respecté', 'SLA met')}{' '}
              <SortIcon columnKey="performance" data-id="element-5575" data-mp="2txq8adnzytdv" />
            </th>
            <th className="px-4 py-2.5 font-medium cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => handleSort('status')} data-id="element-5576" data-mp="2ozoua75fhk99">
              {t('État', 'Status')} <SortIcon columnKey="status" data-id="element-5577" data-mp="1kkq81z55p5d1" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-id="element-5578" data-mp="1gkzitzva2xam">
          {data.map(row => <tr key={row.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-5579" data-mp="1blmo6sy60lxh">
              <td className="px-4 py-3" data-id="element-5580" data-mp="28aucg8q65953">
                <div className="font-medium text-[#0F172A]" data-id="element-5581" data-mp="28aki68y3cftl">
                  {lang === 'fr' ? row.service : row.serviceEn}
                </div>
                <div className="text-xs text-slate-500" data-id="element-5582" data-mp="1za5u51tqsmme">
                  {lang === 'fr' ? row.category : row.categoryEn}
                </div>
              </td>
              <td className="px-4 py-3 text-slate-500" data-id="element-5583" data-mp="1ub31kguy88vb">
                {lang === 'fr' ? row.target : row.targetEn}
              </td>
              <td className="px-4 py-3" data-id="element-5584" data-mp="iwfohqmz5qan">
                <div className="flex items-center gap-3" data-id="element-5585" data-mp="ived1l21r1se">
                  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-5586" data-mp="8shduje4xxqw">
                    <div className={`h-full rounded-full ${getBarColor(row.performance)}`} style={{
                  width: `${row.performance}%`
                }} data-id="element-5587" data-mp="drbx1mg0lobj" />
                  </div>
                  <span className="text-slate-700 font-medium w-10" data-id="element-5588" data-mp="nig9ofi7wx37">
                    {row.performance} %
                  </span>
                </div>
              </td>
              <td className="px-4 py-3" data-id="element-5589" data-mp="wgsbldojbhay">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(row.status)}`} data-id="element-5590" data-mp="wjmu2znzms1q">
                  {statusLabel(row.status)}
                </span>
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}
/** Average each service's performance over the chosen sectors. */
function aggregateServices(sectorIds: string[]): ServiceData[] {
  const perSector = sectorIds.map(sectorServices);
  return initialData.map((row, i) => {
    const performance = Math.round(perSector.reduce((sum, rows) => sum + rows[i].performance, 0) / perSector.length);
    return {
      ...row,
      performance,
      status: statusForPerformance(performance)
    };
  });
}
interface ScopeOption {
  id: string;
  label: Localized;
}
/** One slice of the analysis: a sector, or a sector ∩ district. */
interface ScopeUnit {
  key: string;
  letter: string;
  /** Share of the base sector's volume in this slice. */
  share: number;
  baseSectorId: string;
}
function DistrictDetailPanel({
  mode,
  showSecondary,
  primaryOptions,
  primaryId,
  onSelectPrimary,
  secondaryOptions,
  activeSecondaryIds,
  onToggleSecondary,
  onSelectAllSecondary,
  units,
  scopeLabel
}: {
  mode: 'district' | 'sector';
  showSecondary: boolean;
  primaryOptions: ScopeOption[];
  primaryId: string;
  onSelectPrimary: (id: string) => void;
  secondaryOptions: ScopeOption[];
  activeSecondaryIds: string[];
  onToggleSecondary: (id: string) => void;
  onSelectAllSecondary: () => void;
  units: ScopeUnit[];
  scopeLabel: Localized;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const rows = aggregateServices(units.map(u => u.key));
  const reaches = units.map(u => sectorReach(u.key, u.letter));
  const reach = {
    reachable: Math.round(reaches.reduce((sum, r) => sum + r.reachable, 0) / reaches.length),
    activeCards: Math.round(reaches.reduce((sum, r, i) => sum + r.activeCards * units[i].share, 0)),
    openRate: Math.round(reaches.reduce((sum, r) => sum + r.openRate, 0) / reaches.length)
  };
  const totalRequests = Math.round(units.reduce((sum, u) => sum + sectorRequests(u.baseSectorId) * u.share, 0));
  const allSelected = activeSecondaryIds.length === secondaryOptions.length;
  const isDistrictMode = mode === 'district';
  const primaryTitle = isDistrictMode && showSecondary ? t('District', 'District') : t('Secteur sélectionné', 'Selected sector');
  const secondaryTitle = t('Secteurs', 'Sectors');
  return <div className="border border-slate-200 rounded-xl shadow-sm bg-white flex flex-col h-full overflow-hidden font-body" data-id="element-5591" data-mp="lzf06skrl8kd">
      <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3" data-id="element-5592" data-mp="h62eaq7gzfvb">
        <div className="flex flex-col gap-1 shrink-0" data-id="element-5593" data-mp="c8trgcbdmnnq">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide" data-id="element-5594" data-mp="7a9257xnxoe2">
            <MapPin className="w-3.5 h-3.5" data-id="element-5595" data-mp="2amoixdxa64q" /> {primaryTitle}
          </div>
          <label className="sr-only" htmlFor="scope-select" data-id="element-5596" data-mp="3ueotpcal95gj">
            {primaryTitle}
          </label>
          <select id="scope-select" value={primaryId} onChange={e => onSelectPrimary(e.target.value)} className="-ml-1 text-xl font-bold text-[#0F172A] font-heading bg-transparent rounded-md px-1 py-0.5 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30 cursor-pointer" data-id="element-5597" data-mp="3pfvixrmorsk2">
            {primaryOptions.map(o => <option key={o.id} value={o.id} data-id="element-5598" data-mp="206l570fjnwj1">
                {o.label[lang]}
              </option>)}
          </select>
        </div>

        {showSecondary && <div className="flex flex-col gap-1.5 min-w-0 flex-1" data-id="element-5599" data-mp="24zxg67raw4tb">
            <div className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-5600" data-mp="29w4mi1nlzibs">
              {secondaryTitle}
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-5601" data-mp="1cb05oqtbzcb4">
              {secondaryOptions.length > 1 && <button type="button" onClick={onSelectAllSecondary} aria-pressed={allSelected} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${allSelected ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-5602" data-mp="1h9tngvfnhph3">
                  {t('Tous', 'All')}
                </button>}
              {secondaryOptions.map(o => {
            const on = activeSecondaryIds.includes(o.id);
            return <button key={o.id} type="button" onClick={() => onToggleSecondary(o.id)} aria-pressed={on} className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`} data-id="element-5603" data-mp="1bj8j2jd24d5v">
                    {o.label[lang]}
                  </button>;
          })}
            </div>
          </div>}
      </div>

      <div className="py-4 border-b border-slate-100 flex flex-col gap-3" data-id="element-5604" data-mp="3r9v3e5vepb4u">
        <div className="px-5 flex items-center justify-between gap-3" data-id="element-5605" data-mp="26t3xrw46ni18">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-5606" data-mp="26r11wvgcmuol">
            {t('Performance par service', 'Performance by service')}
          </div>
          <div className="text-xs text-slate-500" data-id="element-5607" data-mp="1xtqrtdf9pbl9">
            {totalRequests.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}{' '}
            {t('requêtes', 'requests')}
          </div>
        </div>
        <PerformanceTable rows={rows} data-id="element-5608" data-mp="2qhcvsmseqi8n" />
      </div>

      <div className="px-5 py-4 border-b border-slate-100 flex flex-col gap-3" data-id="element-5609" data-mp="2uiydqmjstmpp">
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide" data-id="element-5610" data-mp="2ginjd9ja5pzs">
          {t('Portée numérique bciti+', 'bciti+ digital reach')}
        </div>
        <div className="grid grid-cols-3 gap-3" data-id="element-5611" data-mp="2lg70qib9yivy">
          <div className="flex flex-col gap-0.5" data-id="element-5612" data-mp="152yxklhrix0k">
            <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5613" data-mp="15270154lup7n">
              {reach.reachable} %
            </div>
            <div className="text-xs text-slate-500 leading-tight" data-id="element-5614" data-mp="21q66o4t2t5zy">
              {t('Citoyens joignables', 'Reachable citizens')}
            </div>
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-5615" data-mp="1wpi7gvup1iun">
            <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5616" data-mp="1rrqctu2nzimr">
              {reach.activeCards.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
            </div>
            <div className="text-xs text-slate-500 leading-tight" data-id="element-5617" data-mp="2kh76ev20t5kk">
              {t('Cartes citoyennes actives', 'Active citizen cards')}
            </div>
          </div>
          <div className="flex flex-col gap-0.5" data-id="element-5618" data-mp="2aqulh1qanadw">
            <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5619" data-mp="2apji330rg4nb">
              {reach.openRate} %
            </div>
            <div className="text-xs text-slate-500 leading-tight" data-id="element-5620" data-mp="25porz1ot7j3b">
              {t('Ouverture des notifications', 'Notification open rate')}
            </div>
          </div>
        </div>
      </div>

    </div>;
}
/** Readable period, e.g. "30 derniers jours" or "Du 12 août au 25 sept. 2026". */
function periodLabelFor(range: DateRange): Localized {
  if (range.preset !== 'custom') return PERIOD_LABELS[range.preset];
  const f = (iso: string, locale: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  return {
    fr: `Du ${f(range.from, 'fr-CA')} au ${f(range.to, 'fr-CA')}`,
    en: `${f(range.from, 'en-CA')} – ${f(range.to, 'en-CA')}`
  };
}
export function RequetesAnalytics({
  onOpenRequests
}: {
  onOpenRequests?: () => void;
} = {}) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    city
  } = useCity();
  const {
    account
  } = useAccount();
  const cityDistricts = districtsFor(city);
  const hasDistricts = city === 'Brossard';
  const citySectors = sectorsFor(city);

  // Access scope from the signed-in account's job: a councillor only sees
  // their own district; the mayor and directors see the whole city.
  const lockedDistrict = hasDistricts && account.scope.kind === 'district' ? cityDistricts.find(d => d.id === `d${(account.scope as {
    district: number;
  }).district}`) ?? null : null;
  const districts = lockedDistrict ? [lockedDistrict] : cityDistricts;
  const allSectors = lockedDistrict ? lockedDistrict.sectors : citySectors;
  const scopeFactor = lockedDistrict ? lockedDistrict.sectors.reduce((sum, s) => sum + sectorShare(lockedDistrict, s.id), 0) / Math.max(1, citySectors.length) : 1;
  const scopeSeed = lockedDistrict ? hashOf(lockedDistrict.id) : 0;
  const [mode, setMode] = useState<'district' | 'sector'>('district');
  const [mapExpanded, setMapExpanded] = useState(false);
  const [showPins, setShowPins] = useState(false);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [slaFilter, setSlaFilter] = useState<SlaState | null>(null);
  const [districtId, setDistrictId] = useState<string>(districts[0]?.id ?? '');
  const [sectorId, setSectorId] = useState<string>(allSectors[0]?.id ?? '');
  const [secondaryIds, setSecondaryIds] = useState<string[]>([]);
  const district = districts.find(d => d.id === districtId) ?? districts[0];
  const sector = allSectors.find(s => s.id === sectorId) ?? allSectors[0];
  const isSectorMode = hasDistricts && mode === 'sector';
  const primaryOptions: ScopeOption[] = isSectorMode ? allSectors.map(s => ({
    id: s.id,
    label: s.label
  })) : districts.map(d => ({
    id: d.id,
    label: d.label
  }));
  const secondaryOptions: ScopeOption[] = isSectorMode ? [] : (district?.sectors ?? []).map(s => ({
    id: s.id,
    label: s.label
  }));
  const kept = secondaryIds.filter(id => secondaryOptions.some(o => o.id === id));
  const activeSecondaryIds = kept.length > 0 ? kept : secondaryOptions.map(o => o.id);
  const units: ScopeUnit[] = isSectorMode ? sector ? [{
    key: sector.id,
    letter: letterOf(sector.label.fr),
    share: lockedDistrict ? sectorShare(lockedDistrict, sector.id) : 1,
    baseSectorId: sector.id
  }] : [] : activeSecondaryIds.map(sid => {
    const s = allSectors.find(x => x.id === sid);
    return {
      key: sid,
      letter: s ? letterOf(s.label.fr) : '',
      // A sector split between districts only brings its own part.
      share: district ? sectorShare(district, sid) : 1,
      baseSectorId: sid
    };
  });
  const selectPrimary = (id: string) => {
    if (isSectorMode) setSectorId(id);else setDistrictId(id);
    setSecondaryIds([]);
  };
  const toggleSecondary = (id: string) => {
    const next = activeSecondaryIds.includes(id) ? activeSecondaryIds.filter(x => x !== id) : [...activeSecondaryIds, id];
    if (next.length > 0) setSecondaryIds(next);
  };
  /** Switch the map view, carrying the current selection over. */
  const changeView = (next: 'district' | 'sector') => {
    if (next === mode) return;
    if (next === 'sector') {
      const nextSector = activeSecondaryIds[0] ?? district?.sectors[0]?.id;
      if (nextSector) setSectorId(nextSector);
    } else if (sector) {
      // The district holding the largest part of the sector.
      const owner = districts.filter(d => d.sectors.some(s => s.id === sector.id)).sort((a, b) => sectorShare(b, sector.id) - sectorShare(a, sector.id))[0];
      if (owner) setDistrictId(owner.id);
    }
    setSecondaryIds([]);
    setMode(next);
  };
  const highlightedDistrictIds = isSectorMode ? [] : district ? [district.id] : [];
  const pinSectorKey = units.map(u => u.baseSectorId).join(',');
  const pins = useMemo(() => {
    if (!showPins) return [];
    // Sample data has no dates: keep a share of requests proportional to the period.
    const share = Math.min(1, 0.25 + rangeDays(dateRange) / 40);
    return requestPinsFor(city, pinSectorKey.split(',')).filter(
    // The few late / at-risk requests are still open, so they show on every period.
    p => p.state !== 'ok' || p.number % 100 / 100 < share);
  }, [showPins, city, pinSectorKey, dateRange]);
  // Switching account resets the selection to what the new person may see.
  useEffect(() => {
    setSecondaryIds([]);
    setSelectedPinId(null);
    setSlaFilter(null);
  }, [account.id]);

  // Detail closes on its own when the pin leaves the map (details off, other scope).
  const selectedPin = pins.find(p => p.id === selectedPinId && (slaFilter === null || p.state === slaFilter)) ?? null;

  // Large map: the detail sits below it, so jump to it like an anchor.
  const detailRef = useRef<HTMLDivElement>(null);
  const selectedPinKey = selectedPin?.id ?? null;
  const expandedRef = useRef(mapExpanded);
  expandedRef.current = mapExpanded;
  useEffect(() => {
    if (!expandedRef.current || !selectedPinKey) return;
    const id = window.setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 50);
    return () => window.clearTimeout(id);
  }, [selectedPinKey]);
  // One set of demo figures feeds the KPIs, ATNA, the situations and the report.
  const lockedId = lockedDistrict?.id ?? null;
  const periodLabel = periodLabelFor(dateRange);
  const stats = statsFor(lockedId, rangeDays(dateRange));
  const briefing = buildBriefing(stats, lockedId);
  const attention = [...briefing.issues, briefing.result];
  const [reportOpen, setReportOpen] = useState(false);
  const closeReport = useCallback(() => setReportOpen(false), []);
  const [atnaQuestion, setAtnaQuestion] = useState<{
    id: number;
    text: string;
  } | null>(null);
  const askAtna = (q: Localized) => setAtnaQuestion({
    id: Date.now(),
    text: q[lang]
  });
  const mapSectionRef = useRef<HTMLElement>(null);
  const scrollToMap = () => mapSectionRef.current?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
  /** Turn on the pins, optionally filter by state, and scroll to the map. */
  const showOnMap = (state: SlaState | null) => {
    setShowPins(true);
    setSlaFilter(state);
    setSelectedPinId(null);
    if (state && hasDistricts && !lockedDistrict) {
      // Late and at-risk requests sit in sector B (district 3).
      setMode('district');
      setDistrictId('d3');
      setSecondaryIds([]);
    }
    scrollToMap();
  };
  const openDossiers = (target: AttentionTarget) => {
    if (target === 'map-late') showOnMap('late');else if (target === 'map-risk') showOnMap('risk');else if (target === 'map-open') showOnMap(null);else scrollToMap();
  };
  const onKpi = (target: KpiTarget) => {
    if (target === 'requests') {
      if (onOpenRequests) onOpenRequests();else askAtna(ATNA_DATA_SUGGESTIONS[1]);
    } else if (target === 'open') showOnMap(null);else if (target === 'late') showOnMap(stats.late > 0 ? 'late' : null);else if (target === 'sla') scrollToMap();else askAtna(ATNA_DATA_SUGGESTIONS[3]);
  };
  const scopeLabel: Localized = isSectorMode ? sector.label : district?.label ?? {
    fr: '',
    en: ''
  };
  return <div className="space-y-10 animate-in fade-in duration-300" data-id="element-5621" data-mp="wrwbqe0s3871">
      <section className="flex flex-col gap-2" data-id="element-5622" data-mp="wus8w04sxsuf">
        <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-5623" data-mp="rudywcjn48tr">
          {t('Suivi des demandes citoyennes', 'Citizen request tracking')}
        </h2>
        <p className="text-slate-500 text-sm font-body" data-id="element-5624" data-mp="1ki0dsgybizvx">
          {t('Volume, délais et satisfaction des demandes citoyennes, et les dossiers qui demandent votre attention.', 'Volume, turnaround and satisfaction of citizen requests, and the files that need your attention.')}
        </p>
        <DashboardScope name={`${account.firstName} ${account.lastName}`} role={account.role[lang]} city={city} district={lockedDistrict} dateRange={dateRange} onDateRangeChange={setDateRange} periodLabel={periodLabel} data-id="element-5625" data-mp="16r526wwfq4on" />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch" data-id="element-5626" data-mp="2i4s9as41sujr">
        <DashboardKpis stats={stats} periodLabel={periodLabel} onSelect={onKpi} data-id="element-5627" data-mp="2mx1uxvy4r8g8" />
        {/* Height follows the KPI grid; the conversation scrolls inside. */}
        <div className="relative" data-id="element-5628" data-mp="285j7jd2xybmu">
          <AtnaDataAssistant scope={{
          label: lockedDistrict ? lockedDistrict.label : null,
          districtId: lockedId,
          period: periodLabel,
          stats
        }} externalQuestion={atnaQuestion} data-id="element-5629" data-mp="2d6gx4aqa67yd" />
        </div>
      </div>

      <section ref={mapSectionRef} className="flex flex-col gap-4 scroll-mt-6" data-id="element-5630" data-mp="2qyn5han6zan9">
        <div className="flex flex-col gap-1" data-id="element-5631" data-mp="2vx83r4ivvk92">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5632" data-mp="2vvx0d5tcoeih">
            {lockedDistrict ? t(`Respect des SLA · ${lockedDistrict.label.fr}`, `SLA compliance · ${lockedDistrict.label.en}`) : t('Respect des SLA sur le territoire', 'SLA compliance across the territory')}
          </h2>
          <p className="text-sm text-slate-500 font-body" data-id="element-5633" data-mp="3tritc6ldy9sw">
            {t('Comparez le respect des SLA par district, secteur et service, et repérez les requêtes en retard sur la carte.', 'Compare SLA compliance by district, sector and service, and spot late requests on the map.')}
          </p>
        </div>
        <div className={`grid grid-cols-1 gap-6 ${mapExpanded ? '' : 'lg:grid-cols-2'}`} data-id="element-5634" data-mp="3fx8ag5w2l9ur">
          <DistrictMap expanded={mapExpanded} onToggleExpanded={() => setMapExpanded(v => !v)} showDetails={showPins} onToggleDetails={() => setShowPins(v => !v)} pins={pins} selectedPinId={selectedPin?.id ?? null} onPinClick={id => setSelectedPinId(cur => cur === id ? null : id)} dateRange={dateRange} onDateRangeChange={setDateRange} slaFilter={slaFilter} onSlaFilterChange={setSlaFilter} key={`map-${account.id}`} homePoints={lockedDistrict ? districtPoints(lockedDistrict) : undefined} homeLabel={lockedDistrict ? t(`Recentrer sur le ${lockedDistrict.label.fr}`, `Recenter on ${lockedDistrict.label.en}`) : undefined} districts={districts} highlightedDistrictIds={highlightedDistrictIds} overlaySectors={isSectorMode ? allSectors : district?.polygon ? district.sectors : []} activeSectorIds={isSectorMode ? [sector.id] : activeSecondaryIds} focusPoints={isSectorMode ? sector.polygon : district ? districtPoints(district) : []} onDistrictClick={selectPrimary} onSectorClick={isSectorMode ? selectPrimary : undefined} view={isSectorMode ? 'sector' : 'district'} onViewChange={hasDistricts ? changeView : undefined} data-id="element-5635" data-mp="3ayf18pntg50e" />
          {selectedPin ? <div ref={detailRef} className="scroll-mt-6" data-id="element-5636" data-mp="kqnmlbwfh46">
              <RequestDetailPanel pin={selectedPin} onClose={() => setSelectedPinId(null)} data-id="element-5637" data-mp="5mg1s9uf4awh" />
            </div> : units.length > 0 && <DistrictDetailPanel mode={isSectorMode ? 'sector' : 'district'} showSecondary={hasDistricts && !isSectorMode} primaryOptions={primaryOptions} primaryId={isSectorMode ? sector.id : district?.id ?? ''} onSelectPrimary={selectPrimary} secondaryOptions={secondaryOptions} activeSecondaryIds={activeSecondaryIds} onToggleSecondary={toggleSecondary} onSelectAllSecondary={() => setSecondaryIds([])} units={units} scopeLabel={scopeLabel} data-id="element-5638" data-mp="t9fpimks3qgz" />}
        </div>
      </section>

      <TerritorialBriefingPanel key={`briefing-${account.id}`} briefing={briefing} scopeLabel={lockedDistrict ? lockedDistrict.label : null} onOpen={openDossiers} data-id="element-5639" data-mp="1alw522vun976" />

      <div className="flex justify-center pt-2 pb-4 border-t border-slate-200" data-id="element-5640" data-mp="10u02ofmqcwx5">
        <button onClick={() => setReportOpen(true)} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium rounded-lg shadow-sm transition-colors" data-id="element-5641" data-mp="wu7tq1tfcor9">
          <FileText className="w-4 h-4" data-id="element-5642" data-mp="mx3760vo2r9x" />
          {t('Générer le rapport pour le conseil municipal', 'Generate the city council report')}
        </button>
      </div>

      <CouncilReportDialog open={reportOpen} onClose={closeReport} scopeLabel={lockedDistrict ? lockedDistrict.label : null} periodLabel={periodLabel} priorities={attention} kpis={[{
      label: {
        fr: 'Demandes reçues',
        en: 'Requests received'
      },
      value: fmtNum(stats.received, lang)
    }, {
      label: {
        fr: 'Résolues dans les délais',
        en: 'Resolved on time'
      },
      value: `${stats.onTime}${lang === 'fr' ? ' %' : '%'}`
    }, {
      label: {
        fr: 'Délai médian de résolution',
        en: 'Median resolution time'
      },
      value: `${fmtDec(stats.medianDays, lang)} ${lang === 'fr' ? 'j' : 'd'}`
    }, {
      label: {
        fr: 'Satisfaction après traitement',
        en: 'Satisfaction after handling'
      },
      value: `${fmtDec(stats.satisfaction, lang)} / 5`
    }, {
      label: {
        fr: 'Demandes en cours (à ce jour)',
        en: 'Open requests (today)'
      },
      value: fmtNum(stats.open, lang)
    }, {
      label: {
        fr: 'Demandes en retard (à ce jour)',
        en: 'Late requests (today)'
      },
      value: fmtNum(stats.late, lang)
    }]} data-id="element-5643" data-mp="2h7qde58cujfr" />
    </div>;
}